import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { BillingApiService, PaymentRow, PendingPayment } from 'src/app/core/services/billing-api.service';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import type { TenantRow } from 'src/app/core/services/tenant-onboarding-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { PageBodyPanelComponent } from 'src/app/shared/page-body-panel/page-body-panel.component';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-payments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
    IconModule,
    HasPermissionPipe,
    PageBodyPanelComponent,
  ],
  templateUrl: './payments.component.html',
})
export class PaymentsComponent implements OnInit {
  private readonly api = inject(BillingApiService);
  private readonly tenantApi = inject(TenantOnboardingApiService);

  clients: TenantRow[] = [];
  payments: PaymentRow[] = [];
  loading = false;
  error = '';

  // filters
  statusFilter = '';
  methodFilter = '';
  tenantFilter: number | null = null;

  // pagination
  page = 1;
  readonly pageSize = PAGE_SIZE;
  total = 0;

  selected: PaymentRow | null = null;

  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  // Offline receipts waiting for an operator's decision (global across clients).
  pending: PendingPayment[] = [];
  paymentActioning = false;
  actionMsg = '';
  actionErr = '';

  readonly pendingColumns: ListModuleColumnDef[] = [
    { id: 'client', header: 'Client' },
    { id: 'invoice', header: 'Invoice' },
    { id: 'amount', header: 'Amount' },
    { id: 'method', header: 'Method' },
    { id: 'reference', header: 'Reference' },
    { id: 'paid_on', header: 'Paid on' },
    { id: 'actions', header: '', align: 'right' },
  ];

  readonly statuses = ['CREATED', 'PAID', 'FAILED', 'PENDING_VERIFICATION', 'REJECTED'];

  /** What the money was for: the plan name on a subscription purchase, otherwise the invoice type. */
  purposeLabel(row: PaymentRow): string {
    const type = (row.invoice?.invoice_type ?? '').toUpperCase();
    if (type === 'SUBSCRIPTION') {
      return row.invoice?.subscription?.subscription_name
        ? `Subscription · ${row.invoice.subscription.subscription_name}`
        : 'Subscription';
    }
    return type ? type.charAt(0) + type.slice(1).toLowerCase() : '—';
  }

  readonly columns: ListModuleColumnDef[] = [
    { id: 'created_at', header: 'Date' },
    { id: 'client', header: 'Client' },
    { id: 'invoice', header: 'Invoice' },
    { id: 'purpose', header: 'For' },
    { id: 'method', header: 'Method' },
    { id: 'status', header: 'Status' },
    { id: 'amount', header: 'Amount', align: 'right' },
    { id: 'actions', header: '', align: 'right' },
  ];

  // Surface toolbar: status dropdown built-in; tenant + method go in the toolbarFiltersExtra slot.
  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: false,
      status: {
        label: 'Status',
        options: [
          { value: '', label: 'All statuses' },
          { value: 'CREATED', label: 'Created' },
          { value: 'PAID', label: 'Paid' },
          { value: 'FAILED', label: 'Failed' },
          { value: 'PENDING_VERIFICATION', label: 'Pending verification' },
          { value: 'REJECTED', label: 'Rejected' },
        ],
      },
    },
    pagination: true,
  };

  get listFilters(): ListModuleFilters {
    return { ...DEFAULT_LIST_MODULE_FILTERS, status: this.statusFilter };
  }

  get meta(): ListModulePaginationMeta {
    return { total: this.total, lastPage: this.totalPages, currentPage: this.page, perPage: this.pageSize };
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    if (patch.status !== undefined) this.statusFilter = patch.status;
    this.page = 1;
    this.load();
  }

  onListPageChange(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page = next;
    this.load();
  }

  ngOnInit(): void {
    this.tenantApi.listTenants({ page: 1, page_size: 500 }).subscribe({
      next: (res) => (this.clients = res?.data?.result ?? []),
    });
    this.load();
    this.loadPending();
  }

  clientLabel(t: TenantRow): string {
    return t.tenant_name ?? t.unique_id ?? `Client #${t.id}`;
  }

  loadPending(): void {
    this.api.pendingPayments().subscribe({
      next: (r) => (this.pending = r?.data?.items ?? []),
      error: () => {
        this.pending = [];
        this.actionErr = 'Failed to load pending payments.';
      },
    });
  }

  confirmPayment(id: number): void {
    this.runPaymentAction(this.api.confirmPayment(id));
  }

  rejectPayment(id: number): void {
    const reason = globalThis.prompt('Reason for rejecting this payment:')?.trim();
    if (!reason) return;
    this.runPaymentAction(this.api.rejectPayment(id, reason));
  }

  // Confirm and reject change both the queue and the list below it.
  private runPaymentAction(obs: Observable<{ message: string }>): void {
    this.actionMsg = '';
    this.actionErr = '';
    this.paymentActioning = true;
    obs.subscribe({
      next: (r) => {
        this.paymentActioning = false;
        this.actionMsg = r.message;
        this.loadPending();
        this.load();
      },
      error: (err) => {
        this.paymentActioning = false;
        this.actionErr = (err as { error?: { message?: string } })?.error?.message ?? 'Action failed.';
      },
    });
  }

  onFilterChange(): void {
    this.page = 1;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .listPayments({
        status: this.statusFilter || undefined,
        method: this.methodFilter || undefined,
        tenant_id: this.tenantFilter ?? undefined,
        page: this.page,
        page_size: this.pageSize,
      })
      .subscribe({
        next: (r) => {
          this.payments = r?.data?.items ?? [];
          this.total = r?.data?.total ?? 0;
          this.loading = false;
        },
        error: () => {
          this.payments = [];
          this.error = 'Could not load payments.';
          this.loading = false;
        },
      });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  openDetails(p: PaymentRow): void {
    this.selected = p;
  }

  closeDetails(): void {
    this.selected = null;
  }

  viewReceipt(id: number): void {
    this.api.paymentReceipt(id).subscribe({
      next: (r) => {
        if (r?.data?.url) {
          globalThis.open(r.data.url, '_blank');
        } else {
          this.error = 'Receipt URL unavailable.';
        }
      },
      error: () => (this.error = 'Could not load the receipt.'),
    });
  }

  tenantLabel(p: PaymentRow): string {
    return p.tenant?.tenant_name ?? `Client #${p.tenant_id}`;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'PAID':
        return 'bg-success/20 text-success';
      case 'FAILED':
      case 'REJECTED':
        return 'bg-danger/20 text-danger';
      case 'PENDING_VERIFICATION':
        return 'bg-warning/20 text-warning';
      default:
        return 'bg-primary/10 text-primary';
    }
  }
}
